import classes from "./ManageGifCard.module.scss";
import { IGif } from "services/types";
import { Link } from "react-router-dom";
import { routes } from "routes";
import { MoreButton } from "components/buttons";
import { dateHR } from "utils/dates";
import { countHR } from "utils/count";
import { visibilityHR } from "utils/visibility";
import { ManageTags } from "./ManageTags";
import { IconSecondaryButton } from "components/core/IconSecondaryButton";
import tick from "components/icons/tick.svg";
import { className } from "utils/strings";
import { useState, MouseEvent, Fragment, useCallback, memo } from "react";
import { GifOptions } from "./GifOptions";
import { ShareGif } from "./ShareGif";
import DeleteGifPopup from "components/UI/Popups/DeleteGifPopup";
import { useChangeGifsPrivacyMutation, useChangeGifTagsMutation } from "queries";
import AddToNichePopup from "components/UI/Popups/AddToNichePopup";
import { getGifIds } from "utils/gif";
import { EditGalleryPopup } from "../UI/Popups/EditGalleryPopup";
import { EmbedCodePopup } from "components/UI/Popups/EmbedCodePopup";
import { getFixedEmbedCode, getResponsiveEmbedCode } from "../../utils/share";

interface IProps {
  gif: IGif;
  onSelect: (id: string) => void;
  isSelected: boolean;
}

export const ManageGifCard = memo((props: IProps) => {
  const { gif, onSelect, isSelected } = props;

  const changeGifTags = useChangeGifTagsMutation();
  const changeGifsPrivacy = useChangeGifsPrivacyMutation();

  const [
    { isOpen: isOpenOptions, anchorEl: anchorOptionsEl },
    setOptionsData
  ] = useState<{isOpen: boolean; anchorEl: HTMLElement | null}>({
    isOpen: false,
    anchorEl: null
  });

  const [
    { isOpen: isOpenShare, anchorEl: anchorShareEl },
    setShareData
  ] = useState<{isOpen: boolean; anchorEl: HTMLElement | null}>({
    isOpen: false,
    anchorEl: null
  });

  const [isOpenDelete, setOpenDelete] = useState<boolean>(false);
  const [isOpenNiche, setOpenNiche] = useState<boolean>(false);
  const [isOpenEditGallery, setOpenEditGallery] = useState<boolean>(false);
  const [isOpenEmbed, setOpenEmbed] = useState<boolean>(false);

  const handleChangeTags = useCallback(async (tags: string[]) => {
    const ids = await getGifIds([gif]);

    changeGifTags.mutate({
      ids,
      tags
    });
  }, [changeGifTags, gif]);

  const handleSelect = useCallback((id: string) => () => {
    onSelect(id);
  }, [onSelect]);

  const handleOpenOptions = useCallback((event: MouseEvent<HTMLElement>) => {
    setOptionsData({
      isOpen: true,
      anchorEl: event.target as HTMLElement
    });
  }, []);

  const handleCloseOptions = useCallback(() => {
    setOptionsData({
      isOpen: false,
      anchorEl: null
    });
  }, []);

  const handleOpenShare = useCallback(() => {
    handleCloseOptions();

    setShareData({
      isOpen: true,
      anchorEl: anchorOptionsEl
    });
  }, [handleCloseOptions, anchorOptionsEl]);

  const handleCloseShare = useCallback(() => {
    setShareData({
      isOpen: false,
      anchorEl: null
    });
  }, []);

  const handleOpenDelete = useCallback(() => {
    handleCloseOptions();

    setOpenDelete(true);
  }, [handleCloseOptions]);

  const handleCloseDelete = useCallback(() => {
    setOpenDelete(false);
  }, []);

  const handleOpenNiche = useCallback(() => {
    handleCloseOptions();
    setOpenNiche(true);
  }, [handleCloseOptions]);

  const handleCloseNiche = useCallback(() => {
    setOpenNiche(false);
  }, []);

  const handleChangeVisibility = useCallback(async () => {
    handleCloseOptions();

    const ids = await getGifIds([gif]);

    changeGifsPrivacy.mutate({
      ids,
      value: gif.published ? 0 : 1,
    });
  }, [handleCloseOptions, changeGifsPrivacy, gif]);

  const handleOpenEditGallery = useCallback((gallery?: string) => {
    handleCloseOptions();
    if (!gallery) {
      return;
    }

    setOpenEditGallery(true);
  }, [handleCloseOptions]);

  const handleCloseEditGallery = useCallback(() => {
    setOpenEditGallery(false);
  }, []);

  const handleOpenEmbedPopup = useCallback(() => {
    handleCloseShare();
    setOpenEmbed(true);
  }, [handleCloseShare]);

  const handleCloseEmbedPopup = useCallback(() => {
    setOpenEmbed(false);
  }, []);

  return (
    <Fragment>
      <div className={className({
        [classes.card]: true,
        [classes.cardSelected]: isSelected
      })}>
        <div className={classes.preview}>
          <Link to={routes.watch(gif.id)}>
            <img className={classes.previewImage} src={gif.urls.thumbnail} width="100%" height="100%" alt=""/>
            <span className={classes.shadow} />
          </Link>

          <div className={classes.selection}>
            <IconSecondaryButton
              url={isSelected ? tick : undefined}
              variant={isSelected ? "active" : "default"}
              onClick={handleSelect(gif.id)}
            />
          </div>
        </div>
        <div className={classes.info}>
          <div className={classes.infoItem}>
            <div className={classes.infoItemLabel}>Likes</div>
            <div className={classes.infoItemValue}>{countHR(gif.likes)}</div>
          </div>
          <div className={classes.infoItem}>
            <div className={classes.infoItemLabel}>Views</div>
            <div className={classes.infoItemValue}>{countHR(gif.views)}</div>
          </div>
          <div className={classes.infoItem}>
            <div className={classes.infoItemLabel}>Visibility</div>
            <div className={classes.infoItemValue}>{visibilityHR(gif.published)}</div>
          </div>
          <div className={classes.infoItem}>
            <div className={classes.infoItemLabel}>Date</div>
            <div className={classes.infoItemValue}>{dateHR(gif.createDate)}</div>
          </div>
        </div>
        <div className={classes.controls}>
          <MoreButton onClick={handleOpenOptions}/>
        </div>
        <div className={classes.tags}>
          <ManageTags tags={gif.tags} onChange={handleChangeTags} />
        </div>
      </div>
      {
        isOpenOptions && (
          <GifOptions
            gif={gif}
            anchorEl={anchorOptionsEl}
            onShare={handleOpenShare}
            onAddToNiche={handleOpenNiche}
            onChangeVisibility={handleChangeVisibility}
            onDelete={handleOpenDelete}
            onClose={handleCloseOptions}
            onEditGallery={gif.gallery ? handleOpenEditGallery : undefined}
          />
        )
      }

      {
        isOpenShare && (
          <ShareGif
            gif={gif}
            anchorEl={anchorShareEl}
            onOpenEmbed={handleOpenEmbedPopup}
            onClose={handleCloseShare}
          />
        )
      }

      {
        isOpenEmbed && (
          <EmbedCodePopup
            isOpen={true}
            togglePopup={handleCloseEmbedPopup}
            fixedEmbedCode={getFixedEmbedCode(props.gif)}
            responsiveEmbedCode={getResponsiveEmbedCode(props.gif)}
          />
        )
      }

      {
        isOpenDelete && (
          <DeleteGifPopup
            gifs={[gif]}
            isOpen={true}
            onClose={handleCloseDelete}
          />
        )
      }

      {
        isOpenNiche && <AddToNichePopup
          isOpen={true}
          togglePopup={handleCloseNiche}
          gif={gif}
        />
      }

      {
        isOpenEditGallery && gif.gallery && (
          <EditGalleryPopup
            galleryID={gif.gallery}
            onClose={handleCloseEditGallery}
          />
        )
      }
    </Fragment>
  );
});
